<template>
	<div class="torir-spectrogram">
		<div class="--plot">
			<AxisY
				:height="height"
				:min="freqMin"
				:max="freqMax"
			/>
			<div
				ref="scroll"
				class="--scroll"
			>
				<div
					ref="vbox"
					class="--vbox"
					:style="{ height: `${height}px` }"
					@click="handleClick"
				>
					<div
						class="--cursor"
						:style="{ left: `${cursorPosition}px` }"
					/>
					<div
						v-if="inProgress"
						class="--progress"
					>
						Processing...
						<div
							class="--progress-bar"
							:style="{ width: `${progress}%` }"
						/>
					</div>
					<div
						ref="image"
						class="--image"
					/>
					<div class="--markers">
						<MarkerView
							v-for="marker in markers"
							:key="marker.id"
							:marker="marker"
							:style="markerPosition(marker)"
						/>
					</div>
				</div>
				<AxisX
					:width="width"
					:min="timeOffset"
					:max="timeOffset + (duration || 0)"
				/>
			</div>
		</div>
	</div>
</template>

<script>
/* global WaveSurfer */
import { colorMaps } from './colormap'
import AxisX from './components/AxisX.vue'
import AxisY from './components/AxisY.vue'
import MarkerView from './components/MarkerView.vue'
import Marker from './lib/Marker'

/**
 * @vue-prop {TorirSpectrogramOption} [options={}] - オプション
 */
export default {
	name: 'TorirSpectrogram',
	components: {
		AxisX,
		AxisY,
		MarkerView
	},
	props: {
		options: {
			type: Object,
			default: () => ({})
		}
	},
	data () {
		return {
			// WaveSurfer
			wsContainer: document.createElement('div'),
			ws: null,
			mute: false,
			playbackRate: 1,
			volume: 1,
			// View options
			timeOffset: 0, // [s]
			duration: null, // [s]
			scaleX: 1,
			pxPerSec: 256,
			width: 256,
			height: 256,
			fftSamples: 512,
			freqSampling: 48000,
			freqMin: 0,
			freqMax: 24000,
			valueRange: {
				decibelMin: -60,
				decibelMax: 60,
				decibelBoost: -30,
				ratioBoost: 2,
				decibelClip: 30,
				ratioClip: 2
			},
			colorMap: colorMaps[0].values,
			colorMapName: colorMaps[0].name,
			// UI
			playbackPosition: -1,
			clickHandlers: [],
			markers: [],
			inProgress: false,
			progress: 0,
			// misc
			destroyed: false,
			colorMaps
		}
	},
	computed: {
		cursorPosition () {
			if (!this.ws) return -1
			return this.playbackPosition * this.pxPerSec * this.scaleX
		}
	},
	mounted () {
		this.readOptions()

		this.ws = WaveSurfer.create({
			container: this.wsContainer,
			minPxPerSec: this.pxPerSec,
			fillParent: false,
			plugins: [
				WaveSurfer.spectrogram.create({
					container: this.$refs.image,
					labels: false,
					pixelRatio: 1,
					fftSamples: this.fftSamples,
					colorMap: this.colorMap,
					valueRange: this.valueRange,
					onProgressUpdate: this.handleProgressUpdate.bind(this)
				})
			],
			...(this.options.audioContext ? { audioContext: this.options.audioContext } : {})
		})
		this.ws.setMute(this.mute)
		this.ws.setPlaybackRate(this.playbackRate)
		this.ws.setVolume(this.volume)
		this.ws.on('redraw', () => {
			this.freqSampling = this.ws.backend.buffer.sampleRate
			this.freqMax = Math.min(this.freqMax, this.freqSampling / 2)
			this.duration = this.ws.getDuration()
			this.updateView()
		})
		window.ws = this.ws

		this.observePlaybackPosition()
	},
	beforeUnmount () {
		this.destroyed = true
		this.ws.destroy()
	},
	methods: {
		/**
		 * オプションをバリデートしつつ読み込む
		 * @private
		 */
		readOptions () {
			const o = this.options
			if (typeof o.timeOffset === 'number') {
				this.timeOffset = o.timeOffset
			}
			if (typeof o.colorMapName === 'string') {
				this.setColorMap(o.colorMapName)
			}
			if (typeof o.currentTime === 'number') {
				this.setCurrentTime(o.currentTime)
			}
			if (typeof o.fftSamples === 'number') {
				this.setFftSamples(o.fftSamples)
			}
			if (typeof o.volume === 'number') {
				this.setVolume(o.volume)
			}
			{
				const freqMax = (typeof o.frequencyMax === 'number') ? o.frequencyMax : 24000
				const freqMin = (typeof o.frequencyMin === 'number') ? o.frequencyMin : 0
				this.setFrequencyRange(freqMin, freqMax)
			}
			if (typeof o.height === 'number') {
				this.setHeight(o.height)
			}
			if (typeof o.mute === 'boolean') {
				this.setMute(o.mute)
			}
			if (typeof o.playbackRate === 'number') {
				this.setPlaybackRate(o.playbackRate)
			}
			if (typeof o.volume === 'number') {
				this.setVolume(o.volume)
			}

			{
				const valueRange = { ...this.valueRange }
				let valueRangeUpdated = false
				for (const key of ['decibelMin', 'decibelMax', 'decibelBoost', 'decibelClip', 'ratioBoost', 'ratioClip']) {
					if (typeof o[key] === 'number') {
						valueRange[key] = o[key]
						valueRangeUpdated = true
					}
				}
				if (valueRangeUpdated) {
					this.setValueRange(valueRange)
				}
			}
		},
		/**
		 * 音声ファイルを読み込む
		 * @param {string | HTMLMediaElement} url 音声ファイルの URL または audio 要素
		 */
		load (url) {
			this.ws.load(url)
		},
		/**
		 * 音声を再生する
		 * @param {?number} start 再生開始位置 [秒]
		 * @param {?number} end 再生終了位置 [秒]
		 */
		play (start, end) {
			if (this.ws) this.ws.play(start, end)
		},
		/**
		 * 再生を一時停止する
		 */
		pause () {
			if (this.ws) this.ws.pause()
		},
		/**
		 * 再生を停止する
		 */
		stop () {
			if (this.ws) this.ws.stop()
		},
		/**
		 * 再生位置を指定した秒数だけ先（負の数なら前）に移動する
		 * @param {number} offset スキップする時間 [秒]
		 */
		skip (offset) {
			if (this.ws) this.ws.skip(offset)
		},
		/**
		 * クリックされたことを受け取るハンドラを登録する
		 * @param {Function} handler - handler(time, frequency)
		 */
		addClickHandler (handler) {
			this.removeClickHandler(handler)
			this.clickHandlers.push(handler)
		},
		/**
		 * クリックされたことを受け取るハンドラを削除する
		 * @param {Function} handler - ハンドラ
		 */
		removeClickHandler (handler) {
			const index = this.clickHandlers.findIndex(h => h === handler)
			if (index >= 0) {
				this.clickHandlers.splice(index, 1)
			}
		},
		/**
		 * 現在の再生位置を取得する
		 * @returns {number} 再生位置 [秒]
		 */
		getCurrentTime () {
			return this.ws ? this.ws.getCurrentTime() : 0
		},
		/**
		 * 再生位置を設定する
		 * @param {number} position 再生位置 [秒]
		 */
		setCurrentTime (position) {
			if (this.ws) this.ws.setCurrentTime(position)
		},
		/**
		 * 音声の長さを取得する
		 * @returns {number} 音声の長さ [秒]
		 */
		getDuration () {
			return this.ws ? this.ws.getDuration() : 0
		},
		/**
		 * 現在のミュート状態を取得する
		 * @returns {boolean}
		 */
		getMute () {
			return this.mute
		},
		/**
		 * ミュート状態を設定する
		 * @param {boolean} mute
		 */
		setMute (mute) {
			this.mute = mute
			if (this.ws) this.ws.setMute(mute)
		},
		/**
		 * 現在の再生速度を取得する
		 * @returns {number}
		 */
		getPlaybackRate () {
			return this.playbackRate
		},
		/**
		 * 再生速度を設定する
		 * @param {number} rate 再生速度。0.5 なら半分の速度、2 なら倍速
		 */
		setPlaybackRate (rate) {
			this.playbackRate = rate
			if (this.ws) this.ws.setPlaybackRate(rate)
		},
		/**
		 * 現在の再生音量を取得する
		 * @returns {number}
		 */
		getVolume () {
			return this.volume
		},
		/**
		 * 再生音量を設定する
		 * @param {number} newVolume 音量（0 から 1）
		 */
		setVolume (newVolume) {
			this.volume = newVolume
			if (this.ws) this.ws.setVolume(newVolume)
		},
		/**
		 * 現在再生中かどうかを取得する
		 * @returns {boolean}
		 */
		isPlaying () {
			if (!this.ws) return false
			return this.ws.isPlaying()
		},
		/**
		 * カラーマップの一覧を取得する
		 * @returns {ColorMapDesc[]}
		 */
		getColorMaps () {
			// deep copy
			return this.colorMaps.map(item => Object.assign({}, item))
		},
		/**
		 * カラーマップの設定を行う
		 * @param {string} name カラーマップ名
		 */
		setColorMap (name) {
			const index = this.colorMaps.findIndex(cm => cm.name === name)
			if (index < 0) throw new RangeError('Unknown color map: ' + name)

			this.colorMapName = name
			this.colorMap = this.colorMaps[index].values

			if (this.ws) {
				const { spectrogram } = this.ws
				spectrogram.colorMap = this.colorMap
				spectrogram.renderWithCache()
			}
		},
		/**
		 * 時間方向の表示倍率を設定する
		 * @param {number} scale 倍率
		 */
		zoom (scale) {
			if (scale < 0.1 || scale > 10) throw new RangeError()
			this.scaleX = scale
			this.updateView()
		},
		/**
		 * スペクトログラムの高さを設定する
		 * @param {number} height 高さ [px]
		 */
		setHeight (height) {
			if (height < 1 || height > 1024) throw new RangeError()
			this.height = height
			this.updateView()
		},
		/**
		 * 表示周波数範囲を設定する
		 * @param {number} min 最小周波数 [Hz]
		 * @param {number} max 最大周波数 [Hz]
		 */
		setFrequencyRange (min = 0, max = 24000) {
			if (min < 0 || max < min) throw new RangeError()
			const fRange = this.freqSampling / 240
			const clippedMax = Math.max(fRange, Math.min(this.freqSampling / 2, max))
			const clippedMin = Math.min(min, clippedMax - fRange)
			this.freqMax = clippedMax
			this.freqMin = clippedMin
			this.updateView()
		},
		/**
		 * 表示する値域（最大値、最小値、ブースト、クリップ）を設定する
		 * @param {ValueRange} range 値の範囲
		 */
		setValueRange (range) {
			if (range.decibelMax <= range.decibelMin) throw new RangeError()
			this.valueRange = range

			if (!this.ws) return
			const { spectrogram } = this.ws
			spectrogram.valueRange = range
			spectrogram.render()
		},
		/**
		 * FFT のサンプル点数を設定する
		 * 値は 2 のべき乗でないといけない
		 * @param {number} fftSamples サンプル点数
		 */
		setFftSamples (fftSamples) {
			if (this.fftSamples === fftSamples) return
			this.fftSamples = fftSamples

			if (!this.ws) return
			const { spectrogram } = this.ws
			spectrogram.fftSamples = fftSamples
			spectrogram.height = fftSamples / 2
			spectrogram.render()
			this.updateView()
		},
		/**
		 * マーカー一覧を取得する
		 * @returns {Array<Marker>}
		 */
		getMarkerList () {
			return this.markers.map(marker => new Marker(marker.id, marker.time, marker.frequency, marker.usePoint, marker.text))
		},
		/**
		 * マーカーを追加する
		 * @param {number} time 時刻
		 * @param {number} frequency 周波数
		 * @param {boolean} usePoint 打点あり
		 * @param {?string} text テキスト
		 * @returns {number} マーカー id
		 */
		addMarker (time, frequency, usePoint = true, text = null) {
			const marker = Marker.create(time, frequency, usePoint, text)
			this.markers.push(marker)
			return marker.id
		},
		/**
		 * マーカーを削除する
		 * @param {number} markerId マーカー id
		 */
		removeMarker (markerId) {
			const markerIndex = this.markers.findIndex(marker => marker.id === markerId)
			if (markerIndex < 0) return
			this.markers.splice(markerIndex, 1)
		},
		/**
		 * すべてのマーカーを削除する
		 */
		clearMarkers () {
			this.markers.splice(0, this.markers.length)
		},
		/**
		 * マーカーの表示位置を計算する
		 * @param {Marker} marker
		 * @returns {object}
		 */
		markerPosition (marker) {
			const x = this.xFromTime(marker.time)
			const y = this.yFromFrequency(marker.frequency)
			return {
				left: `${x}px`,
				top: `${y}px`
			}
		},
		/**
		 * 表示を更新する
		 * @private
		 */
		updateView () {
			const { vbox, image } = this.$refs
			const duration = this.duration
			if (!duration) {
				vbox.style.height = `${this.height}px`
				image.style.transform = 'none'
				return
			}
			this.duration = duration
			const fScaleY = (this.freqSampling / 2) / (this.freqMax - this.freqMin)
			const scaleY = fScaleY * this.height / (this.fftSamples / 2)
			const offsetY = this.height * fScaleY * (this.freqMax - (this.freqSampling / 2)) / (this.freqSampling / 2)
			this.width = Math.floor(this.scaleX * this.pxPerSec * duration)
			vbox.style.width = `${this.width}px`
			vbox.style.height = `${this.height}px`
			image.style.transform = `translateY(${offsetY}px) scale(1, ${scaleY})`
			const canvas = image.querySelector('canvas')
			if (canvas) {
				canvas.style.width = `${this.duration * this.pxPerSec * this.scaleX}px`
				canvas.style.height = `${this.fftSamples / 2}px`
			}
		},
		/**
		 * 再生位置を監視する
		 * @private
		 */
		observePlaybackPosition () {
			this.playbackPosition = this.ws.getCurrentTime()
			requestAnimationFrame(this.observePlaybackPosition)
		},
		/**
		 * ハンドラが登録されていればクリックされたことを通知する
		 * @private
		 * @param {MouseEvent} event
		 */
		handleClick (event) {
			if (this.clickHandlers.length < 1) return
			/** @type {HTMLDivElement} */
			const vbox = this.$refs.vbox
			const vboxRect = vbox.getBoundingClientRect()
			const time = this.timeFromX(event.clientX - vboxRect.left)
			const frequency = this.frequencyFromY(event.clientY - vboxRect.top)
			this.clickHandlers.forEach(handler => { handler(time, frequency, event) })
		},
		/**
		 * x 座標から時刻に変換する
		 * @private
		 * @param {number} x x 座標
		 * @returns {number} 時刻
		 */
		timeFromX (x) {
			return x / (this.pxPerSec * this.scaleX)
		},
		/**
		 * 時刻から x 座標に変換する
		 * @private
		 * @param {number} time 時刻
		 * @returns {number} x 座標
		 */
		xFromTime (time) {
			return time * this.pxPerSec * this.scaleX
		},
		/**
		 * y 座標から周波数に変換する
		 * @private
		 * @param {number} y y 座標
		 * @returns {number} 周波数
		 */
		frequencyFromY (y) {
			return (this.freqMax - this.freqMin) * (1 - y / this.height) + this.freqMin
		},
		/**
		 * 周波数から y 座標に変換する
		 * @private
		 * @param {number} frequency 周波数
		 * @returns {number} y 座標
		 */
		yFromFrequency (frequency) {
			return this.height * (1 - (frequency - this.freqMin) / (this.freqMax - this.freqMin))
		},
		/**
		 * @param {boolean} done 完了していれば true
		 * @param {number} progress 進捗状況を 0 から  100 の値で
		 */
		handleProgressUpdate (done, progress) {
			this.inProgress = !done
			this.progress = progress
		}
	}
}
</script>

<style lang="scss" scoped>
.torir-spectrogram {
	&, & * {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}
	& ul,
	& ol {
		list-style: none;
	}
}

.--plot {
	display: flex;
}

.--axis {
	font-family: 'Verdana', sans-serif;
	font-size: 10px;
	line-height: 1;
}

.--axis-y {
	width: 50px;
	margin-top: -5px;
}

.--scroll {
	flex: 1;
	width: 100%;
	overflow-x: auto;
	overflow-y: hidden;
}

.--vbox {
	position: relative;
	overflow: hidden;
}

.--image {
	background: #eee;
	transform-origin: 0 0;
	image-rendering: crisp-edges;
}

.--markers {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
}

.--cursor {
	position: absolute;
	z-index: 1;
	top: 0;
	bottom: 0;
	width: 1px;
	background: #fff;
	mix-blend-mode: exclusion;
}

.--progress {
	position: absolute;
	z-index: 10;
	left: 10px;
	top: 10px;
	height: 14px;
	font-family: 'Verdana', sans-serif;
	font-size: 10px;
	line-height: 1;
	color: #fff;
	// border: solid 1px #fff;
}
</style>
